import i18n from "@/i18n.ts";
import { ApiSorting } from "@/types/api.types.ts";
import { ApiOrganizationSettingsViewFilter } from "@/types/organization.types.ts";
import {
  ApiAggregationDate,
  CoreFilterType,
  FilterItem as UIKitFilterItem,
  FilterRenderType,
  FilterValueType,
  PeriodLibraryFactory,
} from "@skailer-com/uikit";
import {
  addDays,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from "date-fns";
import { isEqual } from "lodash";

export enum AppFilterType {
  employee = "employee",
  employeeDivision = "employee_division",
  employeeJobProfile = "employee_job_profile",
  employeeJobRole = "employee_job_role",
  employeeDepartment = "employee_department",
  employeeTeam = "employee_team",
  employeeLocation = "employee_location",
  employeeLifecycleStatus = "employee_lifecycle",
  employeeIsManager = "employee_is_manager",
  employeeHiredOn = "employee_hired_on",
  employeeBirthday = "employee_birthday",
  employeeTerminationDate = "employee_termination_date",
  goalOwner = "goal_owner",

  employmentType = "employment_type",
  employmentContract = "employment_contract",
  employmentLegalEntity = "employment_legal_entity",

  disqualificationReason = "disqualification_reason",
  jobs = "jobs",
  jobCreationDate = "job_creation_date",

  approvalRequestStatus = "approval_request_status",
  formParticipantStatus = "form_participant_status",
  timeOffCategory = "time_off_category",
}

export const FilterType = {
  ...CoreFilterType,
  ...AppFilterType,
} as const;

export type FilterTypeValue = CoreFilterType | AppFilterType;
export type FilterItem = UIKitFilterItem<FilterTypeValue>;

export interface AppTypedFilters {
  division_ids?: number[];
  job_profile_ids?: number[];
  job_role_ids?: number[];
  department_ids?: number[];
  team_ids?: number[];
  location_ids?: number[];
  recruiter_ids?: number[];
  job_ids?: number[];
  category_ids?: number[];
  employment_type_ids?: number[];
  employment_contract_ids?: number[];
  legal_entity_ids?: number[];
  request_statuses?: string[];
  search?: string;
  sorting?: ApiSorting;

  disqualification_reasons?: number[];
}

export const filtersLibrary = () => {
  return {
    page_number: {
      name: "page_number",
      type: CoreFilterType.pageNumber,
      valueType: FilterValueType.number,
      renderType: FilterRenderType.none,
      searchParamName: "page",
      hideInFilter: true,
      defaultValue: 1,
    },
    text: {
      name: "text",
      type: CoreFilterType.search,
      valueType: FilterValueType.string,
      renderType: FilterRenderType.none,
      searchParamName: "search",
    },

    period: {
      name: "period",
      type: CoreFilterType.period,
      valueType: FilterValueType.string,
      renderType: FilterRenderType.none,
      hideInFilter: true,
      label: "—",
    },
    date_from: {
      name: "date_from",
      type: CoreFilterType.dateFrom,
      valueType: FilterValueType.date,
      renderType: FilterRenderType.date,
      label: i18n.t("common:date_from"),
    },
    date_to: {
      name: "date_to",
      type: CoreFilterType.dateTo,
      valueType: FilterValueType.date,
      renderType: FilterRenderType.date,
      label: i18n.t("common:date_to"),
    },
    aggregation_date_mode: {
      name: "aggregation_date_mode",
      type: CoreFilterType.aggregationDateMode,
      valueType: FilterValueType.string,
      renderType: FilterRenderType.none,
      defaultValue: ApiAggregationDate.month,
    },

    employee_ids: {
      name: "employee_ids",
      type: AppFilterType.employee,
      valueType: FilterValueType.numberArray,
      renderType: FilterRenderType.custom,
    },
    division_ids: {
      name: "division_ids",
      type: AppFilterType.employeeDivision,
      valueType: FilterValueType.numberArray,
      renderType: FilterRenderType.custom,
    },
    job_profile_ids: {
      name: "job_profile_ids",
      type: AppFilterType.employeeJobProfile,
      valueType: FilterValueType.numberArray,
      renderType: FilterRenderType.custom,
    },
    job_role_ids: {
      name: "job_role_ids",
      type: AppFilterType.employeeJobRole,
      valueType: FilterValueType.numberArray,
      renderType: FilterRenderType.custom,
    },
    department_ids: {
      name: "department_ids",
      type: AppFilterType.employeeDepartment,
      valueType: FilterValueType.numberArray,
      renderType: FilterRenderType.custom,
    },
    team_ids: {
      name: "team_ids",
      type: AppFilterType.employeeTeam,
      valueType: FilterValueType.numberArray,
      renderType: FilterRenderType.custom,
    },
    location_ids: {
      name: "location_ids",
      type: AppFilterType.employeeLocation,
      valueType: FilterValueType.numberArray,
      renderType: FilterRenderType.custom,
    },
    employee_lifecycle_statuses: {
      name: "employee_lifecycle_statuses",
      type: AppFilterType.employeeLifecycleStatus,
      valueType: FilterValueType.stringArray,
      renderType: FilterRenderType.custom,
      keepEmptyArrayInUrl: true,
    },
    employment_type_ids: {
      name: "employment_type_ids",
      type: AppFilterType.employmentType,
      valueType: FilterValueType.numberArray,
      renderType: FilterRenderType.custom,
    },
    employment_contract_ids: {
      name: "employment_contract_ids",
      type: AppFilterType.employmentContract,
      valueType: FilterValueType.numberArray,
      renderType: FilterRenderType.custom,
    },
    legal_entity_ids: {
      name: "legal_entity_ids",
      type: AppFilterType.employmentLegalEntity,
      valueType: FilterValueType.numberArray,
      renderType: FilterRenderType.custom,
    },
    hired_on: {
      name: "hired_on",
      type: AppFilterType.employeeHiredOn,
      valueType: FilterValueType.string,
      renderType: FilterRenderType.custom,
    },
    birthday: {
      name: "birthday",
      type: AppFilterType.employeeBirthday,
      valueType: FilterValueType.string,
      renderType: FilterRenderType.custom,
    },
    termination_date: {
      name: "termination_date",
      type: AppFilterType.employeeTerminationDate,
      valueType: FilterValueType.string,
      renderType: FilterRenderType.custom,
    },

    job_ids: {
      name: "job_ids",
      type: AppFilterType.jobs,
      valueType: FilterValueType.numberArray,
      renderType: FilterRenderType.custom,
    },
    disqualification_reason: {
      name: "disqualification_reason",
      type: AppFilterType.disqualificationReason,
      valueType: FilterValueType.numberArray,
      renderType: FilterRenderType.custom,
    },

    participant_statuses: {
      name: "statuses",
      type: AppFilterType.formParticipantStatus,
      valueType: FilterValueType.stringArray,
      renderType: FilterRenderType.custom,
    },
    request_statuses: {
      name: "request_statuses",
      type: AppFilterType.approvalRequestStatus,
      valueType: FilterValueType.stringArray,
      renderType: FilterRenderType.custom,
    },

    category_ids: {
      name: "category_ids",
      type: AppFilterType.timeOffCategory,
      valueType: FilterValueType.numberArray,
      renderType: FilterRenderType.custom,
    },
    sort: {
      name: "sorting",
      type: CoreFilterType.sort,
      valueType: FilterValueType.sorting,
      renderType: FilterRenderType.none,
    },
  } as const;
};

export const periodsLibrary: PeriodLibraryFactory = () => {
  const today = new Date();

  return {
    last_week_0: {
      title: i18n.t("plurals:plural_last_weeks", { count: 0 }),
      start: startOfWeek(today, { weekStartsOn: 1 }),
      end: endOfWeek(today, { weekStartsOn: 1 }),
      key: "last_week_0",
      aggregation_date_modes: [ApiAggregationDate.day, ApiAggregationDate.week],
    },
    last_week_2: {
      title: i18n.t("plurals:plural_last_weeks", { count: 2 }),
      start: startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }),
      end: endOfWeek(today, { weekStartsOn: 1 }),
      key: "last_week_2",
      aggregation_date_modes: [ApiAggregationDate.day, ApiAggregationDate.week],
    },
    last_week_4: {
      title: i18n.t("plurals:plural_last_weeks", { count: 4 }),
      start: startOfWeek(subWeeks(today, 3), { weekStartsOn: 1 }),
      end: endOfWeek(today, { weekStartsOn: 1 }),
      key: "last_week_4",
      aggregation_date_modes: [ApiAggregationDate.day, ApiAggregationDate.week],
    },

    last_month_0: {
      title: i18n.t("plurals:plural_last_months", { count: 0 }),
      start: startOfMonth(today),
      end: endOfMonth(today),
      key: "last_month_0",
      aggregation_date_modes: [ApiAggregationDate.month],
    },
    last_month_1: {
      title: i18n.t("plurals:plural_last_months", { count: 2 }),
      start: startOfMonth(subMonths(today, 1)),
      end: endOfMonth(today),
      key: "last_month_1",
      aggregation_date_modes: [ApiAggregationDate.month],
    },
    last_month_3: {
      title: i18n.t("plurals:plural_last_months", { count: 3 }),
      start: startOfMonth(subMonths(today, 2)),
      end: endOfMonth(today),
      key: "last_month_3",
      aggregation_date_modes: [ApiAggregationDate.month],
    },
    last_month_6: {
      title: i18n.t("plurals:plural_last_months", { count: 6 }),
      start: startOfMonth(subMonths(today, 5)),
      end: endOfMonth(today),
      key: "last_month_6",
      aggregation_date_modes: [ApiAggregationDate.month],
    },

    last_quarter_0: {
      title: i18n.t("plurals:plural_last_quarters", { count: 0 }),
      start: startOfQuarter(today),
      end: endOfQuarter(today),
      key: "last_quarter_0",
      aggregation_date_modes: [ApiAggregationDate.quarter],
    },
    last_quarter_2: {
      title: i18n.t("plurals:plural_last_quarters", { count: 2 }),
      start: startOfQuarter(subQuarters(today, 1)),
      end: endOfQuarter(today),
      key: "last_quarter_2",
      aggregation_date_modes: [ApiAggregationDate.quarter],
    },
    last_quarter_3: {
      title: i18n.t("plurals:plural_last_quarters", { count: 3 }),
      start: startOfQuarter(subQuarters(today, 2)),
      end: endOfQuarter(today),
      key: "last_quarter_3",
      aggregation_date_modes: [ApiAggregationDate.quarter],
    },

    last_year_0: {
      title: i18n.t("plurals:plural_last_years", { count: 0 }),
      start: startOfYear(today),
      end: endOfYear(today),
      key: "last_year_0",
      aggregation_date_modes: [ApiAggregationDate.year],
    },
    last_year_2: {
      title: i18n.t("plurals:plural_last_years", { count: 2 }),
      start: startOfYear(subYears(today, 1)),
      end: endOfYear(today),
      key: "last_year_2",
      aggregation_date_modes: [ApiAggregationDate.year],
    },
    last_year_3: {
      title: i18n.t("plurals:plural_last_years", { count: 3 }),
      start: startOfYear(subYears(today, 2)),
      end: endOfYear(today),
      key: "last_year_3",
      aggregation_date_modes: [ApiAggregationDate.year],
    },
    year_to_date: {
      title: i18n.t("common:year_to_date"),
      start: startOfYear(today),
      end: endOfDay(today),
      key: "year_to_date",
      aggregation_date_modes: [ApiAggregationDate.year],
    },
    rolling_year: {
      title: i18n.t("common:last_year"),
      start: startOfDay(addDays(subYears(today, 1), 1)),
      end: endOfDay(today),
      key: "rolling_year",
      aggregation_date_modes: [ApiAggregationDate.month],
    },
  };
};
export default periodsLibrary;

const viewFilterFieldByName: Record<string, keyof ApiOrganizationSettingsViewFilter> = {
  [filtersLibrary().job_profile_ids.name]: "job_profile_ids",
  [filtersLibrary().job_role_ids.name]: "job_role_ids",
  [filtersLibrary().department_ids.name]: "department_ids",
  [filtersLibrary().division_ids.name]: "division_ids",
  [filtersLibrary().location_ids.name]: "location_ids",
  [filtersLibrary().employee_lifecycle_statuses.name]: "employee_lifecycle_statuses",
  [filtersLibrary().employment_type_ids.name]: "employment_type_ids",
  [filtersLibrary().employment_contract_ids.name]: "employment_contract_ids",
};

export function buildViewAppFilterSearchParams(
  viewFilter: ApiOrganizationSettingsViewFilter,
  filters: FilterItem[],
  currentParams?: URLSearchParams
): URLSearchParams {
  const searchParams = new URLSearchParams(currentParams);

  for (const filter of filters) {
    const paramName = filter.name;
    if (searchParams.has(paramName)) continue;

    const viewFilterField = viewFilterFieldByName[paramName];
    if (!viewFilterField) continue;

    const paramValue = viewFilter[viewFilterField];
    if (!paramValue || (Array.isArray(paramValue) && !paramValue.length)) continue;

    if (isEqual(paramValue.sort(), filter.defaultValue)) continue;

    searchParams.set(paramName, paramValue.join(","));
  }

  return searchParams;
}
